import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { graphql, createFragmentContainer } from 'react-relay';
import { connect } from 'react-redux';
import get from 'lodash.get';

// components
import { PdpRespPreviewMessageBanner } from '../PdpRespPreviewMessageBanner/PdpRespPreviewMessageBanner';
import { PdpRespPrintHeader } from '../PdpRespPrintHeader/PdpRespPrintHeader';
import { PdpRespSimilarItemsLanding } from '../PdpRespSimilarItems/PdpRespSimilarItemsLanding';
import { PdpRespBreadcrumbs } from '../PdpRespBreadcrumbs/PdpRespBreadcrumbs';
import { PdpRespHeroCarousel } from '../PdpRespHeroCarousel/PdpRespHeroCarousel';
import { PdpRespHeroCarouselPrint } from '../PdpRespHeroCarousel/PdpRespHeroCarouselPrint';
import { PdpRespIconBubblesWrapper } from '../PdpRespIconBubbles/PdpRespIconBubblesWrapper';
import { PdpRespTitle } from '../PdpRespTitle/PdpRespTitle';
import { PdpSharedSidebarFavorites } from '../PdpSharedSidebarFavorites/PdpSharedSidebarFavorites';
import { PriceDisplay } from 'dibs-buyer-price';
import { PdpRespSidebarButtons } from '../PdpRespSidebarButtons/PdpRespSidebarButtons';
import { PdpRespSidebarBlurbs } from '../PdpRespSidebarBlurbs/PdpRespSidebarBlurbs';
import { PdpSharedDescriptionAbout } from '../PdpSharedDescriptions/PdpSharedDescriptionAbout';
import { PdpSharedDescriptionAdditionalOptions } from '../PdpSharedDescriptions/PdpSharedDescriptionAdditionalOptions';
import { PdpRespDetails } from '../PdpRespDetails/PdpRespDetails';
import { PdpRespShippingReturns } from '../PdpRespDetails/PdpRespShippingReturns';
import { PdpSharedDescriptionAboutCreator } from '../PdpSharedDescriptions/PdpSharedDescriptionAboutCreator';
import { PdpSharedDescriptionAboutMaker } from '../PdpSharedDescriptions/PdpSharedDescriptionAboutMaker';
import { PdpRespBuyerAssuranceBox } from '../PdpRespBuyerAssuranceBox/PdpRespBuyerAssuranceBox';
import { PdpRespSidebarSellerInfo } from '../PdpRespSidebarSellerInfo/PdpRespSidebarSellerInfo';
import { PdpRespSidebarContact } from '../PdpRespSidebarContact/PdpRespSidebarContact';
import { PdpRespSimilarItemsGridSeller } from '../PdpRespSimilarItems/PdpRespSimilarItemsGridSeller';
import { PdpRespSimilarItemsFooter } from '../PdpRespSimilarItems/PdpRespSimilarItemsFooter';
import { PdpRespSeoLinks } from '../PdpRespSeoLinks/PdpRespSeoLinks';
import { PdpRespDealerDetails } from '../PdpRespDealerDetails/PdpRespDealerDetails';
import { PdpSharedQuantitySelect } from '../PdpSharedButtons/PdpSharedQuantitySelect';
import { PdpSharedReminderLoader } from '../PdpSharedReminder/PdpSharedReminderLoader';
import { PdpSharedRecentHistoryTracker } from '../PdpSharedRecentHistoryTracker/PdpSharedRecentHistoryTracker';
import { PdpSharedPromoCode } from '../PdpSharedPromoCode/PdpSharedPromoCode';
import { SharedRecentlyViewedItems } from '../../shared/SharedRecentlyViewedItems/SharedRecentlyViewedItems';
import { PdpSharedRestrictedMaterialsLoader } from '../PdpSharedRestrictedMaterials/PdpSharedRestrictedMaterialsLoader';
import { PdpSharedBuyerProtectionBox } from '../PdpSharedBuyerProtectionBox/PdpSharedBuyerProtectionBox';
import { PdpSharedTradeRewardsBenefitCardLoader } from '../PdpSharedTradeRewardsBenefitCard/PdpSharedTradeRewardsBenefitCardLoader';
import { PdpRespQrCode } from '../PdpRespQrCode/PdpRespQrCode';
import { PdpRespSwatchSelector } from '../PdpRespSwatchSelector/PdpRespSwatchSelector';
import { PdpSharedLeadTime } from '../PdpSharedLeadTime/PdpSharedLeadTime';
import { PdpRespPriceNegotiationToastLoader } from '../PdpRespPriceNegotiationToast/PdpRespPriceNegotiationToastLoader';
import { PdpRespRequestHoldToastLoader } from '../PdpRespRequestHoldToast/PdpRespRequestHoldToastLoader';
import { PdpRespTradeNegotiationModalLoader } from '../PdpRespTradeNegotiationModal/PdpRespTradeNegotiationModalLoader';
import { PdpRespExitModalLazy } from '../PdpRespExitModal/PdpRespExitModalLazy';

// callouts
import { NOTIFICATION_KEYS as SHARED_NOTIFICATION_KEYS } from '../PdpSharedNotifications/pdpSharedNotificationConstants';
import { SharedNotificationsWrapper } from '../../shared/SharedNotificationsWrapper';
import { PdpSharedNotificationMultiplePdpVisitsModalLoader } from '../PdpSharedNotifications/PdpSharedNotificationMultiplePdpVisitsModal/PdpSharedNotificationMultiplePdpVisitsModalLoader';
import { PdpSharedSourcingModalLoaderWrapper } from '../PdpSharedModals/PdpSharedSourcingModal/PdpSharedSourcingModalLoaderWrapper';
import { getPrivateOffer } from '../helpers/getPrivateOffer';
import { getSelectedSku } from '../helpers/getSelectedSku';
import { useSelectedSku } from '../hooks/useSelectedSku';

// helpers
import classnames from 'classnames';
import { useContactDealerAutoModal } from 'dibs-buyer-layout/exports/contactDealerLauncher';

// styles
import styles from './PdpRespContainer.scss';
import { SuspectedTradeUserLoader } from '../../components/global/SuspectedTradeUser/SuspectedTradeUserLoader';

/* eslint-disable react/display-name, react/prop-types */
// disable for function as child of PdpRespNotificationsWrapper

export function PdpRespContainerComponent(props) {
    const {
        pdpPrint,
        currency,
        viewer,
        user,
        isPdpRespPreviewPage,
        item,
        displayInfo,
        sellerItems,
        similarGridItems,
        similarLandingItems,
        vipUserRelationship,
        shipmentQuote,
        shipment,
        relayVariables,
        relay,
        transactions,
        onSkuChange,
    } = props;
    const { isMultiSku, itemId, userId } = relayVariables;
    const privateOffer = isMultiSku ? getPrivateOffer(get(props, 'transactions.edges')) : null;
    const defaultSkuId = get(props, 'item.defaultSkuId');
    const [selectedSkuId, setSelectedSkuId] = useSelectedSku(defaultSkuId, privateOffer);
    const selectedSku = getSelectedSku(item, selectedSkuId);
    const isNewAndCustom = get(item, 'itemType') === 'NEW_MADE_TO_ORDER';
    const isSingleSkuNewAndCustom = isNewAndCustom && !isMultiSku;
    const selectedSkuLeadTime = get(selectedSku, 'skuLeadTime');

    useContactDealerAutoModal({
        itemId,
        userId,
        environment: relay.environment,
        currency,
    });

    useEffect(() => {
        onSkuChange(selectedSkuId);
    }, [onSkuChange, selectedSkuId]);

    return (
        <div>
            {/* Preview Page Message Banner */}
            {isPdpRespPreviewPage && <PdpRespPreviewMessageBanner />}

            {/* Print Header */}
            {relayVariables.isClient && (
                <PdpRespPrintHeader itemId={relayVariables.itemId} className={styles.printShow} />
            )}

            {/* Similar Items */}
            <div className={styles.printHide}>
                <PdpRespSimilarItemsLanding
                    relayVariables={{
                        isClient: relayVariables.isClient,
                        showSimilarItems: relayVariables.showSimilarItems,
                    }}
                    item={item}
                    similarItems={similarLandingItems}
                />
            </div>

            {/* Notification reminder loader */}
            <PdpSharedReminderLoader
                relayVariables={{
                    userId: relayVariables.userId,
                    fetchUser: relayVariables.fetchUser,
                    itemId: relayVariables.itemId,
                    isClient: relayVariables.isClient,
                }}
                environment={relay.environment}
                load={!!user}
            />

            {/* PdpBreadcrumbs */}
            <div className={styles.printHide}>
                <PdpRespBreadcrumbs item={item} />
            </div>

            <div className="rowFlex">
                <div className={classnames(styles.topContainer, 'colXs12 colLg7 colXl8')}>
                    {/* Image Carousel */}
                    <div className={styles.printHide}>
                        <PdpRespHeroCarousel
                            itemTitle={item.title || ''}
                            itemImage={item.firstPhotoWebPath || ''}
                            item={item}
                            selectedSkuId={selectedSkuId}
                            user={user}
                            environment={relay.environment}
                            relayVariables={{
                                userId: relayVariables.userId,
                                fetchUser: relayVariables.fetchUser,
                                itemId: relayVariables.itemId,
                                isClient: relayVariables.isClient,
                            }}
                            viewer={viewer || null}
                            disableZoomModal={isPdpRespPreviewPage}
                            disableRequestMoreImagesModal={isPdpRespPreviewPage}
                        />
                    </div>
                    {/* Stripped Image Carousel (print view) */}
                    {relayVariables.isClient && (
                        <div className={styles.printShow}>
                            <PdpRespHeroCarouselPrint item={item} selectedSkuId={selectedSkuId} />
                        </div>
                    )}

                    {/* Social Icons / Print */}
                    <div className={styles.printHide}>
                        <PdpRespIconBubblesWrapper
                            relayVariables={{
                                itemId: relayVariables.itemId,
                                isTrade: relayVariables.isTrade,
                                isClient: relayVariables.isClient,
                            }}
                            skuId={selectedSkuId}
                            item={item}
                            user={user}
                            viewer={viewer}
                        />
                    </div>
                </div>

                {/* Sidebar */}
                <div className={`${styles.topContainer} colXs12 colLg5 colXl4`}>
                    <div className={styles.sidebarContainer}>
                        <div className="rowFlex">
                            {/* Title */}
                            <div className="colXs12 colLg10">
                                <PdpRespTitle item={item} />
                            </div>
                            {/* Favorite / Folder */}
                            {viewer.isFavoritesEnabled && (
                                <div className={`${styles.printHide} colXs2`}>
                                    <PdpSharedSidebarFavorites
                                        environment={relay.environment}
                                        heartIconWrapperClass={styles.heartIconWrapper}
                                        itemTitle={item.title || ''}
                                        itemImage={item.firstPhotoWebPath || ''}
                                        itemId={relayVariables.itemId}
                                        isClient={relayVariables.isClient}
                                        userId={relayVariables.userId}
                                        wrapperClass={styles.favoritesWrapper}
                                    />
                                </div>
                            )}
                        </div>

                        {/* Price */}
                        {isMultiSku && (
                            <PdpRespSwatchSelector
                                currency={currency}
                                item={item}
                                user={user}
                                transactions={transactions}
                                selectedSkuId={selectedSkuId}
                                onClick={skuId => {
                                    setSelectedSkuId(skuId);
                                    onSkuChange(skuId);
                                }}
                                showSwatchDescriptionOnHover
                                showPrice={pdpPrint.printWithPrice}
                            />
                        )}
                        {!isMultiSku && (
                            <div
                                className={classnames({
                                    [styles.printHide]: !pdpPrint.printWithPrice,
                                })}
                            >
                                <PriceDisplay
                                    currency={currency}
                                    displayInfo={displayInfo}
                                    item={item}
                                    page={'pdp'}
                                    sku={null}
                                    transactions={null}
                                />
                                <div className={styles.printHide}>
                                    <PdpSharedPromoCode
                                        displayInfo={displayInfo}
                                        item={item}
                                        itemId={relayVariables.itemId}
                                        user={user}
                                        userId={relayVariables.userId}
                                        viewer={viewer}
                                    />
                                </div>
                            </div>
                        )}
                        {relayVariables.isClient && (
                            <SuspectedTradeUserLoader
                                user={user}
                                item={item}
                                viewer={viewer}
                                itemSearch={null}
                            />
                        )}
                        {isSingleSkuNewAndCustom && selectedSkuLeadTime && (
                            <div className={styles.leadTimeContainer}>
                                <PdpSharedLeadTime leadTime={selectedSkuLeadTime} />
                            </div>
                        )}
                        {/* Item Ctas */}
                        {relayVariables.isClient && (
                            <div className={`${styles.printHide} ${styles.itemCtasWrapper}`}>
                                <PdpSharedQuantitySelect
                                    item={item}
                                    viewer={viewer}
                                    selectedSkuId={selectedSkuId}
                                >
                                    {function quantitySelection(quantitySelected) {
                                        return (
                                            <PdpRespSidebarButtons
                                                currency={currency}
                                                relayVariables={{
                                                    userId: relayVariables.userId,
                                                    isMultiSku: relayVariables.isMultiSku,
                                                    itemId: relayVariables.itemId,
                                                    relationshipUserIds:
                                                        relayVariables.relationshipUserIds,
                                                }}
                                                isClient={relayVariables.isClient}
                                                user={user}
                                                item={item}
                                                viewer={viewer}
                                                displayInfo={displayInfo}
                                                quantitySelected={quantitySelected}
                                                skuId={selectedSkuId}
                                            />
                                        );
                                    }}
                                </PdpSharedQuantitySelect>
                            </div>
                        )}

                        {/* Restricted Materials */}
                        {relayVariables.isClient && (
                            <PdpSharedRestrictedMaterialsLoader
                                environment={relay.environment}
                                itemId={relayVariables.itemId}
                                isClient={relayVariables.isClient}
                            />
                        )}

                        {/* Blurbs */}
                        <PdpRespSidebarBlurbs
                            currency={currency}
                            relayVariables={{
                                userId: relayVariables.userId,
                                itemId: relayVariables.itemId,
                                isTrade: relayVariables.isTrade,
                                isClient: relayVariables.isClient,
                            }}
                            viewer={viewer}
                            item={item}
                            user={user}
                            displayInfo={displayInfo}
                            vipUserRelationship={vipUserRelationship}
                            shipmentQuote={shipmentQuote}
                            shipment={shipment}
                            showLeadTimeBlurb={!isSingleSkuNewAndCustom && !isMultiSku}
                        />

                        {relayVariables.isClient && (
                            <div className={styles.printHide}>
                                <div className={styles.sidebarSection}>
                                    {/* Seller Stats */}
                                    <PdpRespSidebarSellerInfo
                                        seller={item.seller}
                                        viewer={viewer}
                                        relay={relay}
                                        user={user}
                                    />

                                    {/* Questions about this piece? / Contact Dealer */}
                                    <PdpRespSidebarContact
                                        displayInfo={displayInfo}
                                        item={item}
                                        user={user}
                                        viewer={viewer}
                                        itemId={relayVariables.itemId}
                                        userId={relayVariables.userId}
                                    />
                                </div>
                            </div>
                        )}

                        {relayVariables.isClient && relayVariables.isTrade && (
                            <div className={styles.printHide}>
                                <PdpSharedTradeRewardsBenefitCardLoader user={user} />
                            </div>
                        )}
                        <PdpRespQrCode
                            className={styles.printShow}
                            itemId={relayVariables.itemId}
                        />
                    </div>
                </div>
            </div>

            <div className={`rowFlex ${styles.details}`}>
                <div className="colXs12 colXl9">
                    {/* About */}
                    <PdpSharedDescriptionAbout item={item} />
                    {/* Additional Options */}
                    <PdpSharedDescriptionAdditionalOptions user={user} item={item} />
                    {/* Details */}
                    <PdpRespDetails currency={currency} item={item} />
                    {/* Buyer Protection */}
                    <div className={styles.printHide}>
                        <PdpSharedBuyerProtectionBox isTrade={relayVariables.isTrade} withImage />
                    </div>
                    {/* Shipping and Returns */}
                    <PdpRespShippingReturns
                        isClient={relayVariables.isClient}
                        currency={currency}
                        relayVariables={{
                            userId: relayVariables.userId,
                            itemId: relayVariables.itemId,
                        }}
                        viewer={viewer}
                        user={user}
                        item={item}
                        shipmentQuote={shipmentQuote}
                        shipment={shipment}
                        skuId={selectedSkuId}
                    />
                    {/* Creator Content */}
                    {viewer.isAboutTheMakerEnabled && (
                        <div className={styles.printHide}>
                            <PdpSharedDescriptionAboutCreator item={item} />
                        </div>
                    )}
                    {/* Maker Content */}
                    {viewer.isAboutTheMakerEnabled && (
                        <div className={styles.printHide}>
                            <PdpSharedDescriptionAboutMaker item={item} />
                        </div>
                    )}
                </div>
            </div>

            {/* Contact Dealer Details */}
            <div className={styles.printHide}>
                <PdpRespDealerDetails user={user} item={item} viewer={viewer} currency={currency} />
            </div>

            {/* More From Seller */}
            <div className={styles.printHide}>
                <PdpRespSimilarItemsGridSeller
                    relayVariables={{
                        userId: relayVariables.userId,
                        isClient: relayVariables.isClient,
                    }}
                    item={item}
                    sellerItems={sellerItems}
                    viewer={viewer}
                    currency={currency}
                    user={user}
                />
            </div>
            {/* You May Also Like */}
            <div className={styles.printHide}>
                <PdpRespSimilarItemsFooter
                    currency={currency}
                    viewer={viewer}
                    user={user}
                    item={item}
                    similarGridItems={similarGridItems}
                    relayVariables={relayVariables}
                />
            </div>
            {/* Recently Viewed Items */}
            <div className={`${styles.printHide} ${styles.recentlyViewed}`}>
                <SharedRecentlyViewedItems
                    itemId={item.serviceId}
                    isClient={relayVariables.isClient}
                    user={user}
                />
            </div>
            {/* Photos - todo (INTL-70: PDP follow on) */}

            {/* More Ways To Browse */}
            <div className={styles.printHide}>
                <PdpRespSeoLinks item={item} viewer={viewer} />
            </div>
            {relayVariables.isClient && !viewer.disablePdpExitModal && (
                <PdpRespExitModalLazy item={item} />
            )}
            <SharedNotificationsWrapper type={SHARED_NOTIFICATION_KEYS.MULTI_PDP_VISITS}>
                {({ isEnabled }) => (
                    <PdpSharedNotificationMultiplePdpVisitsModalLoader
                        isEnabled={viewer.isFavoritesEnabled && isEnabled && !isPdpRespPreviewPage}
                        itemId={relayVariables.itemId}
                        environment={relay.environment}
                    />
                )}
            </SharedNotificationsWrapper>
            <SharedNotificationsWrapper type={SHARED_NOTIFICATION_KEYS.PDP_SOURCING_MODAL}>
                {({ isEnabled }) => (
                    <PdpSharedSourcingModalLoaderWrapper
                        isOpen={isEnabled}
                        itemId={relayVariables.itemId}
                        environment={relay.environment}
                        showInitialPrompt
                    />
                )}
            </SharedNotificationsWrapper>

            {relayVariables.isTrade && (
                <React.Fragment>
                    {/* Trade Micro Goals Placements */}
                    <SharedNotificationsWrapper
                        type={SHARED_NOTIFICATION_KEYS.PDP_TRADE_NEGOTIATION_TOAST}
                    >
                        {({ isEnabled }) => {
                            if (isEnabled) {
                                return (
                                    <PdpRespPriceNegotiationToastLoader
                                        currency={currency}
                                        displayInfo={displayInfo}
                                        item={item}
                                        userId={relayVariables.userId}
                                    />
                                );
                            }
                            return null;
                        }}
                    </SharedNotificationsWrapper>
                    <SharedNotificationsWrapper
                        type={SHARED_NOTIFICATION_KEYS.PDP_TRADE_HOLD_REQUEST_TOAST}
                    >
                        {({ isEnabled }) => {
                            if (isEnabled) {
                                return (
                                    <PdpRespRequestHoldToastLoader
                                        item={item}
                                        userId={relayVariables.userId}
                                    />
                                );
                            }
                            return null;
                        }}
                    </SharedNotificationsWrapper>
                    <SharedNotificationsWrapper
                        type={SHARED_NOTIFICATION_KEYS.PDP_TRADE_NEGOTIATION_MODAL}
                    >
                        {({ isEnabled }) => {
                            if (isEnabled) {
                                return (
                                    <PdpRespTradeNegotiationModalLoader
                                        environment={relay.environment}
                                        itemId={relayVariables.itemId}
                                        currency={currency}
                                    />
                                );
                            }
                            return null;
                        }}
                    </SharedNotificationsWrapper>
                </React.Fragment>
            )}

            {/* Recent History */}
            {!isPdpRespPreviewPage && <PdpSharedRecentHistoryTracker item={item} />}

            {/* Buyer Assurance Box */}
            {!relayVariables.isTrade && (
                <div className={styles.printHide}>
                    <PdpRespBuyerAssuranceBox user={user} />
                </div>
            )}
        </div>
    );
}

PdpRespContainerComponent.propTypes = {
    currency: PropTypes.string,
    relayVariables: PropTypes.shape({
        userId: PropTypes.string,
        itemId: PropTypes.string.isRequired,
        isClient: PropTypes.bool.isRequired,
        isMultiSku: PropTypes.bool.isRequired,
        isTrade: PropTypes.bool,
        fetchUser: PropTypes.bool.isRequired,
        relationshipUserIds: PropTypes.array.isRequired,
        followSearchPages: PropTypes.array,
        fetchFollowSearch: PropTypes.bool.isRequired,
        skuId: PropTypes.string,
    }).isRequired,
    onSkuChange: PropTypes.func.isRequired,
    relay: PropTypes.object.isRequired,

    // relay response data
    viewer: PropTypes.object.isRequired,
    user: PropTypes.object,
    item: PropTypes.object.isRequired,
    displayInfo: PropTypes.object,
    sellerItems: PropTypes.object,
    similarLandingItems: PropTypes.object,
    similarGridItems: PropTypes.object,
    vipUserRelationship: PropTypes.object,
    shipmentQuote: PropTypes.object,

    // from redux
    pdpPrint: PropTypes.shape({
        printWithPrice: PropTypes.bool.isRequired,
    }).isRequired,
    openTradeAskSellerTooltip: PropTypes.func,
};

function mapStateToProps({ pdpPrint, pdpPreviewPage }) {
    return {
        pdpPrint: {
            printWithPrice: pdpPrint.printWithPrice,
        },
        isPdpRespPreviewPage: pdpPreviewPage.isPdpRespPreviewPage,
    };
}

export const PdpRespContainer = createFragmentContainer(
    connect(mapStateToProps)(PdpRespContainerComponent),
    {
        viewer: graphql`
            fragment PdpRespContainer_viewer on Viewer {
                ...PdpRespSidebarButtons_viewer
                ...PdpRespSidebarBlurbs_viewer
                ...PdpRespShippingReturns_viewer
                ...PdpRespIconBubblesWrapper_viewer
                ...PdpRespSimilarItemsFooter_viewer
                ...PdpRespSimilarItemsGridSeller_viewer
                ...PdpRespHeroCarousel_viewer @include(if: $fetchPdp)
                ...PdpRespSeoLinks_viewer
                ...PdpRespDealerDetails_viewer
                ...PdpRespSidebarContact_viewer
                ...PdpRespSidebarSellerInfo_viewer
                ...PdpSharedPromoCode_viewer
                ...SuspectedTradeUserLoader_viewer
                isAboutTheMakerEnabled: isEnabledByLocale(featureName: "ABOUT_THE_MAKER")
                isFavoritesEnabled: isEnabledByLocale(featureName: "FAVORITES")
                disablePdpExitModal: isEnabled(key: "disablePdpExitModal")
            }
        `,
        user: graphql`
            fragment PdpRespContainer_user on User {
                ...PdpRespSimilarItemsGridSeller_user
                ...PdpRespSimilarItemsFooter_user
                ...PdpRespSidebarButtons_user
                ...PdpRespSidebarBlurbs_user
                ...PdpRespShippingReturns_user
                ...PdpRespBuyerAssuranceBox_user
                ...PdpRespSidebarContact_user
                ...PdpRespHeroCarousel_user
                ...PdpRespIconBubblesWrapper_user
                ...PdpRespDealerDetails_user
                ...PdpRespSidebarSellerInfo_user
                ...PdpSharedPromoCode_user
                ...PdpSharedDescriptionAdditionalOptions_user
                ...PdpSharedTradeRewardsBenefitCardLoader_user
                ...PdpRespSwatchSelector_user @include(if: $isMultiSku)
                ...SharedRecentlyViewedItems_user
                ...SuspectedTradeUserLoader_user
            }
        `,
        item: graphql`
            fragment PdpRespContainer_item on Item {
                serviceId
                firstPhotoWebPath(size: small)
                title
                defaultSkuId
                itemType
                ...PdpRespExitModal_item
                ...PdpRespSimilarItemsLanding_item
                ...PdpRespBreadcrumbs_item
                ...PdpRespHeroCarouselPrint_item
                ...PdpRespIconBubblesWrapper_item
                ...PdpRespTitle_item
                ...PdpRespSidebarButtons_item
                ...PdpSharedContactDealerButton_item
                ...PdpSharedDescriptionAdditionalOptions_item
                ...PdpRespSidebarBlurbs_item
                ...PdpSharedDescriptionAbout_item
                ...PdpSharedDescriptionAboutCreator_item
                ...PdpRespShippingReturns_item
                ...PdpRespSidebarContact_item
                ...PdpRespSeoLinks_item
                ...PdpRespSimilarItemsGridSeller_item
                ...PdpRespSimilarItemsFooter_item
                ...PdpSharedQuantitySelect_item
                ...PdpSharedRecentHistoryTracker_item
                ...PriceDisplay_item @arguments(hasUserId: $fetchUser, userId: $userId)
                ...PdpSharedPromoCode_item
                ...PdpRespSwatchSelector_item @include(if: $isMultiSku)
                ...PdpRespPriceNegotiationToastLoader_item @include(if: $isTrade)
                ...PdpRespRequestHoldToastLoader_item @include(if: $isTrade)
                ...SuspectedTradeUserLoader_item
                seller {
                    ...PdpRespSidebarSellerInfo_seller
                }
                ...PdpRespDealerDetails_item
                ... @include(if: $fetchPdp) {
                    ...PdpRespHeroCarousel_item
                    ...PdpRespDetails_item
                    ...PdpSharedDescriptionAboutMaker_item
                }
                skus {
                    serviceId
                    skuLeadTime: leadTime(pageType: PDP)
                }
            }
        `,
        displayInfo: graphql`
            fragment PdpRespContainer_displayInfo on ItemDisplayInfo {
                ...PdpRespSidebarButtons_displayInfo
                ...PdpRespSidebarContact_displayInfo
                ...PdpSharedPromoCode_displayInfo
                ...PdpRespSidebarBlurbs_displayInfo
                ...PriceDisplay_displayInfo
                ...PdpSharedPromoCode_displayInfo
                ...PdpRespPriceNegotiationToastLoader_displayInfo @include(if: $isTrade)
            }
        `,
        sellerItems: graphql`
            fragment PdpRespContainer_sellerItems on moreLikeThisConnection {
                ...PdpRespSimilarItemsGridSeller_sellerItems
            }
        `,
        similarGridItems: graphql`
            fragment PdpRespContainer_similarGridItems on moreLikeThisConnection {
                ...PdpRespSimilarItemsFooter_similarGridItems
            }
        `,
        similarLandingItems: graphql`
            fragment PdpRespContainer_similarLandingItems on moreLikeThisConnection {
                ...PdpRespSimilarItemsLanding_similarItems
            }
        `,
        vipUserRelationship: graphql`
            fragment PdpRespContainer_vipUserRelationship on UserRelationship {
                ...PdpRespSidebarBlurbs_vipUserRelationship
            }
        `,
        shipmentQuote: graphql`
            fragment PdpRespContainer_shipmentQuote on ShipmentQuote {
                ...PdpRespSidebarBlurbs_shipmentQuote
                ...PdpRespShippingReturns_shipmentQuote
            }
        `,
        shipment: graphql`
            fragment PdpRespContainer_shipment on Shipment {
                ...PdpRespSidebarBlurbs_shipment
                ...PdpRespShippingReturns_shipment
            }
        `,
        transactions: graphql`
            fragment PdpRespContainer_transactions on TransactionConnection {
                ...PdpRespSwatchSelector_transactions @include(if: $isMultiSku)
                edges @include(if: $isMultiSku) {
                    node {
                        skuId
                        isPrivateOffer
                        isCanceled
                    }
                }
            }
        `,
    }
);
